/**
 * Registry of in-flight Firestore writes (v3.56.0).
 *
 * Why: the idle timeout must never sign a teacher out mid-submission. A
 * 1:1 assessment ends with a single addDoc; if that write is still in
 * flight when the idle clock expires, signing out invalidates the token
 * underneath it and the teacher loses the scores they just collected.
 *
 * Deliberately mirrors utils/snapshotRegistry.js (the established pattern
 * for "things that must settle before signOut"): a module-level Set, a
 * register call that returns its own cleanup, and one drain function that
 * App.js awaits before tearing the session down.
 *
 * Usage at a write site — one line, and the promise still resolves/rejects
 * exactly as before:
 *   await trackWrite(addDoc(collection(db, 'assessments'), payload));
 */
const inFlight = new Set();

/**
 * Track a write promise. Returns the SAME promise so callers can keep
 * awaiting/catching normally; settlement (either way) deregisters it.
 */
export function trackWrite(promise) {
  const entry = Promise.resolve(promise)
    .catch(() => { /* swallowed HERE only so the registry never rejects; the caller's own catch still sees the original rejection */ })
    .finally(() => { inFlight.delete(entry); });
  inFlight.add(entry);
  return promise;
}

/** How many writes are currently in flight. */
export function pendingWriteCount() {
  return inFlight.size;
}

/**
 * Resolves once every currently-tracked write has settled. Writes started
 * WHILE draining are awaited too (a submit handler that chains two writes
 * would otherwise slip through the gap).
 *
 * `timeoutMs` bounds the wait: a wedged write must not strand the session
 * signed-in forever on a shared device. Resolves `true` when everything
 * settled, `false` when the timeout won the race.
 */
export function whenWritesSettled({ timeoutMs = 10000 } = {}) {
  if (inFlight.size === 0) return Promise.resolve(true);

  const drain = (async () => {
    // Loop rather than a single Promise.all: settling one write can enqueue
    // the next one in a multi-step submit.
    while (inFlight.size > 0) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(Array.from(inFlight));
    }
    return true;
  })();

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return drain;

  return Promise.race([
    drain,
    new Promise((resolve) => { setTimeout(() => resolve(false), timeoutMs); }),
  ]);
}

/** Test hook. */
export function _resetPendingWrites() {
  inFlight.clear();
}
